import CryptoJS from 'crypto-js';

const SECRET_KEY = process.env.REACT_APP_SIGNATURE_KEY || '';
const REQUIRE_SIGNATURE = process.env.REACT_APP_REQUIRE_SIGNATURE === 'true';

interface DeviceData {
  brandName?: string;
  devicePushToken?: string;
  devicePushTokenType?: string;
  appVersion?: string;
  platform?: 'ios' | 'android';
  notificationsPermission?: boolean;
  gpsPermission?: boolean;
  cameraPermission?: boolean;
  developerModeEnabled?: boolean;
}

interface SecureDeviceData {
  data: DeviceData;
  signature: string;
  timestamp: number;
}

/**
 * Valida assinatura HMAC-SHA256
 */
function validateSignature(data: Record<string, any>, signature: string): boolean {
  if (!SECRET_KEY) {
    console.warn('[SECURITY] Chave secreta não configurada (REACT_APP_SIGNATURE_KEY)');
    return false;
  }

  try {
    const keys = Object.keys(data).sort();
    const sortedData: Record<string, any> = {};
    keys.forEach(key => {
      sortedData[key] = data[key];
    });
    
    const dataString = JSON.stringify(sortedData);
    const expectedSignature = CryptoJS.SHA256(dataString + SECRET_KEY).toString();

    return signature === expectedSignature;
  } catch (error) {
    console.error('[SECURITY] Erro ao validar assinatura:', error);
    return false;
  }
}

/**
 * Obtém dados do dispositivo com validação segura
 * 
 * COMPATIBILIDADE:
 * - App nativo NOVO: retorna dados validados criptograficamente
 * - App nativo ANTIGO: retorna dados não-seguros (localStorage simples)
 * - Browser direto: retorna null
 */
export function getDeviceData(): DeviceData | null {
  try {
    // 1. TENTAR dados seguros (app nativo NOVO)
    const secureDataStr = localStorage.getItem('__secureDeviceData');
    
    if (secureDataStr) {
      console.log('[SECURITY] Dados assinados encontrados - validando...');
      
      let parsed: SecureDeviceData;
      try {
        parsed = JSON.parse(secureDataStr);
      } catch (e) {
        console.error('[SECURITY] ❌ Dados seguros corrompidos');
        
        // Se requer assinatura, falha. Se não, tenta fallback
        if (REQUIRE_SIGNATURE) return null;
        console.log('[SECURITY] Tentando fallback para dados não-seguros...');
        return getLegacyDeviceData();
      }

      const { data, signature, timestamp } = parsed;

      // Verificar campos obrigatórios
      if (!data || !signature || !timestamp) {
        console.error('[SECURITY] ❌ Dados seguros incompletos');
        if (REQUIRE_SIGNATURE) return null;
        return getLegacyDeviceData();
      }

      // Verificar idade (máx 5 minutos)
      const age = Date.now() - timestamp;
      if (age > 5 * 60 * 1000) {
        console.warn('[SECURITY] ⚠️ Dados expirados (', Math.floor(age / 1000), 's)');
        console.warn('[SECURITY] Recomenda-se reiniciar o app');
        // Continua mesmo expirado (não é crítico para compatibilidade)
      }

      // VALIDAR ASSINATURA
      const isValid = validateSignature(data, signature);
      
      if (!isValid) {
        console.error('[SECURITY] ❌❌❌ ASSINATURA INVÁLIDA!');
        console.error('[SECURITY] Dados foram adulterados ou são fraudulentos');
        
        if (REQUIRE_SIGNATURE) {
          return null; // Bloqueia se requer assinatura
        }
        
        console.warn('[SECURITY] Usando dados não-seguros (modo compatibilidade)');
        return getLegacyDeviceData();
      }

      console.log('[SECURITY] ✅ Assinatura válida - dados confiáveis');
      return data;
    }

    // 2. FALLBACK para dados não-seguros (app nativo ANTIGO ou ausente)
    console.log('[SECURITY] Dados assinados não encontrados');
    
    if (REQUIRE_SIGNATURE) {
      console.error('[SECURITY] ❌ App requer assinatura mas não foi fornecida');
      console.error('[SECURITY] Esta versão do app não é compatível');
      return null;
    }

    console.log('[SECURITY] ⚠️ Usando dados não-seguros (modo compatibilidade)');
    return getLegacyDeviceData();

  } catch (error) {
    console.error('[SECURITY] Erro ao obter dados do dispositivo:', error);
    
    if (REQUIRE_SIGNATURE) {
      return null;
    }
    
    return getLegacyDeviceData();
  }
}

/**
 * Obtém dados do localStorage (versão antiga/não-segura)
 * Usado para compatibilidade com apps antigos
 */
function getLegacyDeviceData(): DeviceData | null {
  try {
    // Verificar se está no WebView
    const hasWebViewMarker = (window as any).__isLegitimateWebView;
    
    if (!hasWebViewMarker) {
      console.warn('[SECURITY] ⚠️ Não está rodando no WebView oficial');
      console.warn('[SECURITY] Alguns dados podem não estar disponíveis');
    }

    const brandName = localStorage.getItem('brandName') || undefined;
    
    // Se não tem nem brandName, provavelmente não está no app
    if (!brandName) {
      console.log('[SECURITY] Nenhum dado de dispositivo encontrado');
      console.log('[SECURITY] Aplicação provavelmente não está no WebView');
      return null;
    }

    return {
      brandName,
      devicePushToken: localStorage.getItem('devicePushToken') || undefined,
      devicePushTokenType: localStorage.getItem('devicePushTokenType') || undefined,
      appVersion: localStorage.getItem('appVersion') || undefined,
      platform: (localStorage.getItem('platform') as 'ios' | 'android') || undefined,
      notificationsPermission: localStorage.getItem('notificationsPermission') === 'true',
      gpsPermission: localStorage.getItem('gpsPermission') === 'true',
      cameraPermission: localStorage.getItem('cameraPermission') === 'true',
      developerModeEnabled: localStorage.getItem('developerModeEnabled') === 'true',
    };
  } catch (error) {
    console.error('[SECURITY] Erro ao ler dados legados:', error);
    return null;
  }
}

/**
 * Verifica se ambiente é seguro (não tem modo desenvolvedor)
 */
export function isSecureEnvironment(): boolean {
  const deviceData = getDeviceData();
  
  if (!deviceData) {
    console.warn('[SECURITY] Sem dados do dispositivo - ambiente não confiável');
    return false;
  }

  if (deviceData.developerModeEnabled === true) {
    console.warn('[SECURITY] ⚠️ Modo desenvolvedor detectado');
    return false;
  }

  return true;
}

/**
 * Verifica se dados foram validados criptograficamente
 */
export function hasSecureSignature(): boolean {
  const secureDataStr = localStorage.getItem('__secureDeviceData');
  
  if (!secureDataStr) {
    return false;
  }

  try {
    const parsed = JSON.parse(secureDataStr);
    const { data, signature } = parsed;
    
    return validateSignature(data, signature);
  } catch {
    return false;
  }
}

/**
 * Retorna informações sobre o nível de segurança atual
 */
export function getSecurityInfo() {
  const hasSignature = hasSecureSignature();
  const deviceData = getDeviceData();
  
  return {
    hasValidSignature: hasSignature,
    dataSource: hasSignature ? 'signed' : 'legacy',
    isSecure: isSecureEnvironment(),
    isDeveloperMode: deviceData?.developerModeEnabled === true,
    isWebView: !!(window as any).__isLegitimateWebView,
    deviceData,
    recommendation: hasSignature 
      ? 'Dados validados criptograficamente ✅' 
      : 'Dados não-seguros - atualize o app nativo ⚠️'
  };
}
